"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _core = require("@popperjs/core");
var React = _interopRequireWildcard(require("react"));
var _Portal = _interopRequireDefault(require("../Portal"));
var _setRef = _interopRequireDefault(require("../utilities/setRef"));
var _useForkRef = _interopRequireDefault(require("../utilities/useForkRef"));
var _useIsomorphicLayoutEffect = _interopRequireDefault(require("../utilities/useIsomorphicLayoutEffect"));
const _excluded = ["anchorEl", "children", "container", "disablePortal", "keepMounted", "modifiers", "open", "placement", "popperOptions", "popperRef", "style", "transition"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function isChildrenFunction(children) {
  return typeof children === 'function';
}
function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
const defaultPopperOptions = {};

/**
 * A Popper can be used to display some content on top of another. It's an alternative to react-popper.
 */
const Popper = /*#__PURE__*/React.forwardRef(function Popper(props, ref) {
  const {
      anchorEl,
      children,
      container,
      disablePortal = false,
      keepMounted = false,
      modifiers,
      open,
      placement: initialPlacement = 'bottom',
      popperOptions = defaultPopperOptions,
      popperRef: popperRefProp,
      style,
      transition = false
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded);
  const tooltipRef = React.useRef(null);
  const ownRef = (0, _useForkRef.default)(tooltipRef, ref);
  const popperRef = React.useRef(null);
  const handlePopperRef = (0, _useForkRef.default)(popperRef, popperRefProp);
  const handlePopperRefRef = React.useRef(handlePopperRef);
  (0, _useIsomorphicLayoutEffect.default)(() => {
    handlePopperRefRef.current = handlePopperRef;
  }, [handlePopperRef]);
  // @ts-expect-error
  React.useImperativeHandle(popperRefProp, () => popperRef.current, []);
  const [exited, setExited] = React.useState(true);
  const [placement, setPlacement] = React.useState(initialPlacement);
  React.useEffect(() => {
    if (popperRef.current) {
      popperRef.current.forceUpdate();
    }
  });
  const handleOpen = React.useCallback(() => {
    if (!tooltipRef.current || !anchorEl || !open) {
      return;
    }
    if (popperRef.current) {
      popperRef.current.destroy();
      // @ts-expect-error
      handlePopperRefRef.current(null);
    }
    const handlePopperUpdate = _ref => {
      let {
        placement
      } = _ref;
      setPlacement(placement);
    };
    let popperModifiers = [{
      name: 'preventOverflow',
      options: {
        altBoundary: disablePortal
      }
    }, {
      name: 'flip',
      options: {
        altBoundary: disablePortal
      }
    }, {
      name: 'onUpdate',
      enabled: true,
      phase: 'afterWrite',
      fn(_ref2) {
        let {
          state
        } = _ref2;
        handlePopperUpdate(state);
      }
    }];
    if (modifiers != null) {
      popperModifiers = popperModifiers.concat(modifiers);
    }
    if (popperOptions && popperOptions.modifiers != null) {
      popperModifiers = popperModifiers.concat(popperOptions.modifiers);
    }
    const popper = (0, _core.createPopper)(getAnchorEl(anchorEl), tooltipRef.current, _objectSpread(_objectSpread({
      placement: initialPlacement
    }, popperOptions), {}, {
      modifiers: popperModifiers
    }));

    // @ts-expect-error
    handlePopperRefRef.current(popper);
  }, [anchorEl, disablePortal, modifiers, open, initialPlacement, popperOptions]);
  const handleRef = React.useCallback(node => {
    (0, _setRef.default)(ownRef, node);
    handleOpen();
  }, [ownRef, handleOpen]);
  const handleEnter = () => {
    setExited(false);
  };
  const handleClose = () => {
    if (!popperRef.current) {
      return;
    }
    popperRef.current.destroy();
    // @ts-expect-error
    handlePopperRefRef.current(null);
  };
  const handleExited = () => {
    setExited(true);
    handleClose();
  };
  React.useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);
  React.useEffect(() => {
    if (!open && !transition) {
      // Otherwise handleExited will call this.
      handleClose();
    }
  }, [open, transition]);
  if (!keepMounted && !open && (!transition || exited)) {
    return null;
  }
  const childProps = {
    placement
  };
  if (transition) {
    childProps.TransitionProps = {
      in: open,
      onEnter: handleEnter,
      onExited: handleExited
    };
  }
  return /*#__PURE__*/React.createElement(_Portal.default, {
    disablePortal: disablePortal,
    container: container
  }, /*#__PURE__*/React.createElement("div", (0, _extends2.default)({
    ref: handleRef,
    role: "tooltip"
  }, other, {
    style: _objectSpread({
      // Prevents scroll issue, waiting for Popper.js to add this style once initiated.
      position: 'fixed',
      // Fix Popper.js display issue
      top: 0,
      left: 0,
      // @ts-expect-error
      display: !open && keepMounted && !transition ? 'none' : null
    }, style)
  }), isChildrenFunction(children) ? children(childProps) : children));
});
var _default = exports.default = Popper;